import { PrimaryButton, SmallSecondaryButton } from '@shared/components/buttons';
import { inputClass } from '@shared/components/form-styles';
import { Spinner } from '@shared/components/spinner';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useMemo, useState } from 'react';
import type { Accept } from 'react-dropzone';
import type { Store } from '../../../data/store';
import { useClipboardCopy } from '../../../hooks/use-clipboard-copy';
import type { ReceivingIntake } from '../../../services/receiving';
import { FileDropzone } from './file-dropzone';
import { StepHeading } from './step-heading';

export type GenericReceivingUploadProps = {
  vendorName: string;
  store: Store;
  disabled?: boolean;
  onUploadFile: (file: File) => Promise<void>;
  onProcess: (intake: ReceivingIntake) => Promise<void>;
};

const CSV_ACCEPT: Accept = { 'text/csv': ['.csv'] };

function StepLabel({ children }: { children: ReactNode }) {
  return <h3 className="text-base font-semibold text-gray-900">{children}</h3>;
}

function buildPrompt(vendorName: string, storeName: string): string {
  return [
    `I'm receiving an invoice from ${vendorName} into Orderbot for ${storeName}. It's attached/pasted below. Using the Orderbot tools:`,
    `1. Confirm ${vendorName} supports AI receiving; if not, stop and tell me.`,
    '2. Read every line item from what I gave you.',
    "3. Before building anything, match each line to an existing product in my catalog for this vendor — by item code or barcode first, then a name/size search for the rest. If a match is uncertain or an item looks new, ask me — don't guess.",
    '4. Validate the reconciled rows with the totals printed on the invoice (row count, cases, units, invoice total) and fix whatever it flags.',
    '5. Once it validates cleanly, give me the final CSV to upload — nothing else.',
  ].join('\n');
}

export function GenericReceivingUpload({
  vendorName,
  store,
  disabled = false,
  onUploadFile,
  onProcess,
}: GenericReceivingUploadProps) {
  const prompt = useMemo(() => buildPrompt(vendorName, store.name), [vendorName, store.name]);
  const { copied, copy } = useClipboardCopy(prompt);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (file: File, intake: ReceivingIntake) => {
    setBusy(true);
    setError(null);
    try {
      await onUploadFile(file);
      await onProcess(intake);
    } catch {
      setError('Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  const handleProcessPaste = () => {
    const text = pasted.trim();
    if (!text) {
      return;
    }

    void submit(new File([text], 'ai-receiving.csv', { type: 'text/csv' }), 'paste');
  };

  const handleDrop = (accepted: File[]) => {
    if (accepted[0]) {
      void submit(accepted[0], 'file');
    }
  };

  return (
    <div className="flex flex-col gap-4 min-h-0 overflow-y-auto">
      <StepHeading>Receive with your AI assistant</StepHeading>

      <div>
        <StepLabel>Step 1 · Connect your AI assistant</StepLabel>
        <div className="mt-1 rounded-sm border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <b>{vendorName}</b> doesn&rsquo;t have an automatic invoice format. Use your AI assistant
          (Claude or ChatGPT) to turn whatever you have (a photo, a scan, a screenshot, an email,
          even a spreadsheet) into a receiving file.{' '}
          <Link
            to="/stores/$storeId/ai-assistant"
            params={{ storeId: store.id }}
            target="_blank"
            rel="noreferrer"
            className="text-primary-700 underline"
          >
            How to connect your assistant
          </Link>
          .
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <StepLabel>Step 2 · Send this to your assistant</StepLabel>
          <SmallSecondaryButton
            onClick={() => void copy()}
            aria-pressed={copied}
            aria-label={copied ? 'Copied prompt' : 'Copy prompt'}
          >
            {copied ? 'Copied' : 'Copy'}
          </SmallSecondaryButton>
        </div>
        <div className="rounded-sm border border-indigo-200 bg-indigo-50 p-3">
          <pre
            data-testid="ai-prompt"
            className="whitespace-pre-wrap text-sm text-gray-700 font-mono"
          >
            {prompt}
          </pre>
        </div>
      </div>

      <div>
        <StepLabel>Step 3 · Paste the CSV it gives you</StepLabel>
        <textarea
          data-testid="csv-paste"
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          disabled={disabled || busy}
          rows={6}
          placeholder="item_code,barcode,brand,…"
          className={`${inputClass} mt-1 w-full font-mono text-sm`}
        />
      </div>

      <p className="text-center text-xs text-gray-400">or</p>

      <FileDropzone
        accept={CSV_ACCEPT}
        label="Select a CSV file"
        onDrop={handleDrop}
        multiple={false}
        disabled={disabled || busy}
      />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <PrimaryButton
        type="button"
        disabled={disabled || busy || pasted.trim().length === 0}
        onClick={handleProcessPaste}
      >
        {busy ? <Spinner /> : 'Process invoice'}
      </PrimaryButton>
    </div>
  );
}
